import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from '@/providers/LocationProvider';
import { Plus, Edit, Eye, EyeOff, Trash2 } from 'lucide-react';
import type { Package } from '@/types';
import { toast } from 'sonner';

export default function AdminServices() {
  const location = useLocation();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    base_price_cents: 0,
    same_day_fee_cents: 0,
    service_fee_cents: 0,
    image_url: '',
    // New CSV fields
    sku: '',
    category: '',
    subcategory: '',
    required_credential: '',
    required_kit: '',
    long_description: '',
    filmmaker_rate_per_minute_cents: 0,
    filmmaker_payout_cents: 0,
    transaction_fees_cents: 0,
    insurance_coverage_cents: 0,
    take_rate_cents: 0,
    including_service_fee_cents: 0,
    tax_cents: 0,
    final_client_billing_cents: 0,
    gmv_percentage: null as number | null,
    prep_time_minutes: null as number | null,
    package_type: 'VIDEO_ONLY' as 'VIDEO_ONLY' | 'PHOTO_AND_VIDEO' | 'PHOTO_ONLY',
  });

  useEffect(() => {
    fetchPackages();
  }, [location]);

  const fetchPackages = async () => {
    try {
      const { data: locationData } = await supabase
        .from('locations')
        .select('id')
        .eq('code', location)
        .single();

      if (!locationData) return;

      const { data } = await supabase
        .from('packages')
        .select('*')
        .eq('location_id', locationData.id)
        .order('created_at', { ascending: true });

      setPackages((data as any) || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPackage = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      duration_minutes: 60,
      base_price_cents: 0,
      same_day_fee_cents: 0,
      service_fee_cents: 0,
      image_url: '',
      sku: '',
      category: '',
      subcategory: '',
      required_credential: '',
      required_kit: '',
      long_description: '',
      filmmaker_rate_per_minute_cents: 0,
      filmmaker_payout_cents: 0,
      transaction_fees_cents: 0,
      insurance_coverage_cents: 0,
      take_rate_cents: 0,
      including_service_fee_cents: 0,
      tax_cents: 0,
      final_client_billing_cents: 0,
      gmv_percentage: null,
      prep_time_minutes: null,
      package_type: 'VIDEO_ONLY',
    });
    setIsDialogOpen(true);
  };

  const handleEditPackage = (pkg: any) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      duration_minutes: pkg.duration_minutes || 60,
      base_price_cents: pkg.base_price_cents || 0,
      same_day_fee_cents: pkg.same_day_fee_cents || 0,
      service_fee_cents: pkg.service_fee_cents || 0,
      image_url: pkg.image_url || '',
      sku: pkg.sku || '',
      category: pkg.category || '',
      subcategory: pkg.subcategory || '',
      required_credential: pkg.required_credential || '',
      required_kit: pkg.required_kit || '',
      long_description: pkg.long_description || '',
      filmmaker_rate_per_minute_cents: pkg.filmmaker_rate_per_minute_cents || 0,
      filmmaker_payout_cents: pkg.filmmaker_payout_cents || 0,
      transaction_fees_cents: pkg.transaction_fees_cents || 0,
      insurance_coverage_cents: pkg.insurance_coverage_cents || 0,
      take_rate_cents: pkg.take_rate_cents || 0,
      including_service_fee_cents: pkg.including_service_fee_cents || 0,
      tax_cents: pkg.tax_cents || 0,
      final_client_billing_cents: pkg.final_client_billing_cents || 0,
      gmv_percentage: pkg.gmv_percentage ? parseFloat(pkg.gmv_percentage) : null,
      prep_time_minutes: pkg.prep_time_minutes || null,
      package_type: (pkg.package_type || 'VIDEO_ONLY') as 'VIDEO_ONLY' | 'PHOTO_AND_VIDEO' | 'PHOTO_ONLY',
    });
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (pkg: Package) => {
    try {
      const { error } = await supabase
        .from('packages')
        .update({ is_active: !pkg.is_active })
        .eq('id', pkg.id);

      if (error) throw error;

      toast.success(`Package ${pkg.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchPackages();
    } catch (error) {
      console.error('Error toggling package:', error);
      toast.error('Failed to update package');
    }
  };

  const handleDeletePackage = async (pkg: Package) => {
    if (!confirm(`Are you sure you want to delete "${pkg.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // First, check if package is being used by any bookings
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id')
        .eq('package_id', pkg.id)
        .limit(1);

      if (bookingsError) {
        console.error('Error checking bookings:', bookingsError);
      }

      if (bookings && bookings.length > 0) {
        toast.error('Cannot delete package: It is being used by existing bookings. Deactivate it instead.');
        return;
      }

      // Attempt to delete
      const { data, error } = await supabase
        .from('packages')
        .delete()
        .eq('id', pkg.id)
        .select();

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }

      // Verify deletion by checking if package still exists
      const { data: verifyPackage, error: verifyError } = await supabase
        .from('packages')
        .select('id')
        .eq('id', pkg.id)
        .maybeSingle();

      // If verifyError is not a "not found" error, log it
      if (verifyError && verifyError.code !== 'PGRST116') {
        console.error('Error verifying deletion:', verifyError);
      }

      if (verifyPackage) {
        // Package still exists - deletion was blocked
        console.error('Package still exists after delete attempt');
        toast.error('Package could not be deleted. It may be protected by database constraints. Try deactivating it instead.');
      } else {
        // Successfully deleted (verifyPackage is null)
        toast.success('Package deleted successfully');
        fetchPackages();
      }
    } catch (error: any) {
      console.error('Error deleting package:', error);
      
      // Check for specific error codes
      if (error.code === '23503' || error.message?.includes('foreign key') || error.message?.includes('violates foreign key')) {
        toast.error('Cannot delete package: It is being used by existing bookings. Deactivate it instead.');
      } else if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('RLS')) {
        toast.error('Permission denied: You do not have permission to delete packages. Check RLS policies.');
      } else {
        toast.error(error.message || 'Failed to delete package. Check console for details.');
      }
    }
  };

  const handleSavePackage = async () => {
    try {
      const { data: locationData } = await supabase
        .from('locations')
        .select('id')
        .eq('code', location)
        .single();

      if (!locationData) {
        toast.error('Location not found');
        return;
      }

      // Auto-generate SKU if empty
      let finalSku = formData.sku.trim();
      if (!finalSku) {
        // Find all SKUs with pattern "104-XXX" and get the highest number
        const { data: packages } = await supabase
          .from('packages')
          .select('sku')
          .not('sku', 'is', null)
          .like('sku', '104-%');

        if (packages && packages.length > 0) {
          // Extract numbers from all SKUs and find the maximum
          const numbers = packages
            .map(pkg => {
              const match = pkg.sku?.match(/104-(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            })
            .filter(num => num > 0);

          if (numbers.length > 0) {
            const maxNumber = Math.max(...numbers);
            const nextNumber = maxNumber + 1;
            // Format with leading zeros (3 digits)
            finalSku = `104-${String(nextNumber).padStart(3, '0')}`;
          } else {
            // No valid SKU found, start from 001
            finalSku = '104-001';
          }
        } else {
          // No existing SKU found, start from 001
          finalSku = '104-001';
        }
      }

      const packageData = {
        ...formData,
        sku: finalSku,
        location_id: locationData.id,
        // Ensure null values are properly handled
        gmv_percentage: formData.gmv_percentage ?? null,
        prep_time_minutes: formData.prep_time_minutes ?? null,
        // Clear required_kit if package_type is VIDEO_ONLY
        required_kit: (formData.package_type === 'PHOTO_AND_VIDEO' || formData.package_type === 'PHOTO_ONLY') ? formData.required_kit : null,
      };

      if (editingPackage) {
        // Update existing package
        const { error } = await supabase
          .from('packages')
          .update(packageData)
          .eq('id', editingPackage.id);

        if (error) throw error;
        toast.success('Package updated successfully');
      } else {
        // Create new package
        const { error } = await supabase
          .from('packages')
          .insert([packageData]);

        if (error) throw error;
        toast.success('Package created successfully');
      }

      setIsDialogOpen(false);
      fetchPackages();
    } catch (error: any) {
      console.error('Error saving package:', error);
      toast.error('Failed to save package');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Services</h2>
          <p className="text-muted-foreground">
            Manage packages and pricing
          </p>
        </div>
        <Button onClick={handleNewPackage}>
          <Plus className="h-4 w-4 mr-2" />
          New Service
        </Button>
      </div>

      {loading ? (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">Loading services...</p>
        </Card>
      ) : packages.length === 0 ? (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">No services found</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg">{pkg.name}</h3>
                    <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {pkg.description}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="font-medium">{pkg.duration_minutes} min</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Base Price:</span>
                      <span className="font-medium">${(pkg.base_price_cents / 100).toFixed(2)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Same-day Fee:</span>
                      <span className="font-medium">+${(pkg.same_day_fee_cents / 100).toFixed(2)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Service Fee:</span>
                      <span className="font-medium">${(pkg.service_fee_cents / 100).toFixed(2)}</span>
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button size="sm" variant="outline" onClick={() => handleEditPackage(pkg)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleToggleActive(pkg)}>
                    {pkg.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleDeletePackage(pkg)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Package Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Package' : 'Create New Package'}</DialogTitle>
            <DialogDescription>
              {editingPackage ? 'Update package details and pricing' : 'Add a new service package'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* SKU */}
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU</Label>
                  <Input
                    id="sku"
                    placeholder="104-001"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  />
                </div>

                {/* Image URL */}
                <div className="space-y-2">
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    placeholder="https://example.com/image.jpg"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  />
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="The Glow Session"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Short Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Professional food photography session..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Long Description */}
              <div className="space-y-2">
                <Label htmlFor="long_description">Long Description</Label>
                <Textarea
                  id="long_description"
                  placeholder="Detailed description of the package..."
                  value={formData.long_description}
                  onChange={(e) => setFormData({ ...formData, long_description: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Package Type */}
                <div className="space-y-2">
                  <Label htmlFor="package_type">Package Type *</Label>
                  <select
                    id="package_type"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.package_type}
                    onChange={(e) => setFormData({ ...formData, package_type: e.target.value as 'VIDEO_ONLY' | 'PHOTO_AND_VIDEO' | 'PHOTO_ONLY' })}
                  >
                    <option value="VIDEO_ONLY">VIDEO ONLY</option>
                    <option value="PHOTO_AND_VIDEO">PHOTO AND VIDEO</option>
                    <option value="PHOTO_ONLY">PHOTO ONLY</option>
                  </select>
                </div>

                {/* Required Kit (only for PHOTO AND VIDEO or PHOTO_ONLY) */}
                {(formData.package_type === 'PHOTO_AND_VIDEO' || formData.package_type === 'PHOTO_ONLY') && (
                  <div className="space-y-2">
                    <Label htmlFor="required_kit">Required Kit</Label>
                    <Input
                      id="required_kit"
                      placeholder="Food Photography Kit, Venue & Ambiance Kit..."
                      value={formData.required_kit}
                      onChange={(e) => setFormData({ ...formData, required_kit: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Category */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    placeholder="Retail, Creative, Real Estate..."
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </div>

                {/* Subcategory */}
                <div className="space-y-2">
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <Input
                    id="subcategory"
                    placeholder="Clothing Store / Home Decor Store"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  />
                </div>

                {/* Required Credential */}
                <div className="space-y-2">
                  <Label htmlFor="required_credential">Required Credential</Label>
                  <Input
                    id="required_credential"
                    placeholder="Videographer, Cinematographer..."
                    value={formData.required_credential}
                    onChange={(e) => setFormData({ ...formData, required_credential: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Duration Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Duration</h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Duration */}
                <div className="space-y-2">
                  <Label htmlFor="duration">Service Duration (minutes) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    placeholder="300"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                  />
                </div>

                {/* Prep Time */}
                <div className="space-y-2">
                  <Label htmlFor="prep_time">Prep Time (minutes)</Label>
                  <Input
                    id="prep_time"
                    type="number"
                    min="0"
                    placeholder="60"
                    value={formData.prep_time_minutes || ''}
                    onChange={(e) => setFormData({ ...formData, prep_time_minutes: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Pricing</h3>
              
              <div className="grid grid-cols-3 gap-4">
                {/* Base Price */}
                <div className="space-y-2">
                  <Label htmlFor="base_price">Base Price (CAD) *</Label>
                  <Input
                    id="base_price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="900.00"
                    value={(formData.base_price_cents / 100).toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, base_price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                  />
                </div>

                {/* Same Day Fee */}
                <div className="space-y-2">
                  <Label htmlFor="same_day_fee">Same-day Fee (CAD) *</Label>
                  <Input
                    id="same_day_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={(formData.same_day_fee_cents / 100).toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, same_day_fee_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                  />
                </div>

                {/* Service Fee */}
                <div className="space-y-2">
                  <Label htmlFor="service_fee">Service Fee (CAD) *</Label>
                  <Input
                    id="service_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="5.99"
                    value={(formData.service_fee_cents / 100).toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, service_fee_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                  />
                </div>
              </div>
            </div>

            {/* Financial Details Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Financial Details (in cents)</h3>
              
              <div className="grid grid-cols-3 gap-4">
                {/* Filmmaker Rate per Minute */}
                <div className="space-y-2">
                  <Label htmlFor="filmmaker_rate">Filmmaker Rate/min (cents)</Label>
                  <Input
                    id="filmmaker_rate"
                    type="number"
                    min="0"
                    placeholder="80"
                    value={formData.filmmaker_rate_per_minute_cents}
                    onChange={(e) => setFormData({ ...formData, filmmaker_rate_per_minute_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Filmmaker Payout */}
                <div className="space-y-2">
                  <Label htmlFor="filmmaker_payout">Filmmaker Payout (cents)</Label>
                  <Input
                    id="filmmaker_payout"
                    type="number"
                    min="0"
                    placeholder="24000"
                    value={formData.filmmaker_payout_cents}
                    onChange={(e) => setFormData({ ...formData, filmmaker_payout_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Transaction Fees */}
                <div className="space-y-2">
                  <Label htmlFor="transaction_fees">Transaction Fees (cents)</Label>
                  <Input
                    id="transaction_fees"
                    type="number"
                    min="0"
                    placeholder="4500"
                    value={formData.transaction_fees_cents}
                    onChange={(e) => setFormData({ ...formData, transaction_fees_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Insurance Coverage */}
                <div className="space-y-2">
                  <Label htmlFor="insurance_coverage">Insurance Coverage (cents)</Label>
                  <Input
                    id="insurance_coverage"
                    type="number"
                    min="0"
                    placeholder="1800"
                    value={formData.insurance_coverage_cents}
                    onChange={(e) => setFormData({ ...formData, insurance_coverage_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Take Rate */}
                <div className="space-y-2">
                  <Label htmlFor="take_rate">104 Take Rate (cents)</Label>
                  <Input
                    id="take_rate"
                    type="number"
                    min="0"
                    placeholder="60300"
                    value={formData.take_rate_cents}
                    onChange={(e) => setFormData({ ...formData, take_rate_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Including Service Fee */}
                <div className="space-y-2">
                  <Label htmlFor="including_service_fee">Including Service Fee (cents)</Label>
                  <Input
                    id="including_service_fee"
                    type="number"
                    min="0"
                    placeholder="60899"
                    value={formData.including_service_fee_cents}
                    onChange={(e) => setFormData({ ...formData, including_service_fee_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Tax */}
                <div className="space-y-2">
                  <Label htmlFor="tax">Tax (cents)</Label>
                  <Input
                    id="tax"
                    type="number"
                    min="0"
                    placeholder="11700"
                    value={formData.tax_cents}
                    onChange={(e) => setFormData({ ...formData, tax_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* Final Client Billing */}
                <div className="space-y-2">
                  <Label htmlFor="final_client_billing">Final Client Billing (cents)</Label>
                  <Input
                    id="final_client_billing"
                    type="number"
                    min="0"
                    placeholder="101700"
                    value={formData.final_client_billing_cents}
                    onChange={(e) => setFormData({ ...formData, final_client_billing_cents: parseInt(e.target.value) || 0 })}
                  />
                </div>

                {/* GMV Percentage */}
                <div className="space-y-2">
                  <Label htmlFor="gmv_percentage">GMV Percentage</Label>
                  <Input
                    id="gmv_percentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="68.00"
                    value={formData.gmv_percentage || ''}
                    onChange={(e) => setFormData({ ...formData, gmv_percentage: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePackage}>
              {editingPackage ? 'Save Changes' : 'Create Package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
